// components/UserAvatar.jsx - Web version with profile photo support
import React, { useState, useEffect, useCallback } from 'react';
import Avatar, { genConfig } from 'react-nice-avatar';
import { getUserAvatarConfig } from '../services/avatarService';
import supabase from '../services/supabase';
import '../styles/UserAvatar.scss';

export default function UserAvatar({
  userId,
  size = 40,
  className = '',
  showPhoto = true,
  user = null // Optional: pass user object directly to avoid extra fetch
}) {
  const [config, setConfig] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoError, setPhotoError] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchProfilePhoto = useCallback(async () => {
    if (!userId) return null;

    const { data, error } = await supabase
      .from('users')
      .select('profile_photo_cdn_url, profile_photo_direct_url')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile photo:', error);
      return null;
    }

    return data;
  }, [userId]);

  const loadAvatar = useCallback(async () => {
    try {
      setLoading(true);

      // Load avatar config and profile photo
      const [avatarConfig, profileData] = await Promise.all([
        getUserAvatarConfig(userId),
        showPhoto ? fetchProfilePhoto() : Promise.resolve(null)
      ]);

      setConfig(avatarConfig);
      if (profileData) {
        setProfilePhoto(profileData.profile_photo_cdn_url || profileData.profile_photo_direct_url);
      }
    } catch (error) {
      console.error('Error loading avatar:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, showPhoto, fetchProfilePhoto]);

  const loadAvatarConfig = useCallback(async () => {
    try {
      const targetUserId = user?.id || userId;
      const avatarConfig = await getUserAvatarConfig(targetUserId);
      setConfig(avatarConfig);
    } catch (error) {
      console.error('Error loading avatar config:', error);
      // If there's an error, generate a default config
      setConfig(genConfig());
    }
  }, [user, userId]);

  useEffect(() => {
    const initAvatar = async () => {
      if (user) {
        // If user object is passed directly, use it
        setProfilePhoto(user.profile_photo_cdn_url || user.profile_photo_direct_url);
        if (user.avatar_config) {
          setConfig(user.avatar_config);
        } else {
          // Need to load or generate avatar config
          await loadAvatarConfig();
        }
        setLoading(false);
      } else if (userId) {
        await loadAvatar();
      } else {
        setLoading(false);
      }
    };

    initAvatar();
  }, [userId, user, loadAvatar, loadAvatarConfig]);

  const handlePhotoError = () => {
    console.log('Photo load error, falling back to avatar');
    setPhotoError(true);
  };

  if (loading) {
    return (
      <div
        className={`user-avatar-placeholder ${className}`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: '#e0e0e0'
        }}
      />
    );
  }

  // Show profile photo if available and no error
  if (showPhoto && profilePhoto && !photoError) {
    return (
      <img
        src={profilePhoto}
        alt="Profile"
        className={`user-avatar-photo ${className}`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
        }}
        onError={handlePhotoError}
      />
    );
  }

  // Fall back to avatar config
  if (!config) {
    return (
      <div
        className={`user-avatar-fallback ${className}`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: '#7C4DFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          fontSize: size * 0.4
        }}
      >
        ?
      </div>
    );
  }

  return (
    <div className={`user-avatar ${className}`}>
      <Avatar
        style={{ width: size, height: size }}
        {...config}
      />
    </div>
  );
}
